import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { gql } from '@apollo/client';
import { awsApiAdapter } from 'src/utils/awsApiAdapter';
import {
  ListOrganizationsByNameQuery,
  ListUserByOrganizationIDQuery,
  ListUserByOrganizationIDQueryVariables,
  Organization,
  GetUserQuery,
  User,
  GetOrganizationQuery,
} from 'src/API';
import * as queries from 'coteam4/src/graphql/queries';
import { useError } from './ErrorContext';
import { Skeleton } from '@mui/material';
import { nonNullable } from 'src/utils/typeUtil';
import { AuthContext } from './AwsAuthContext';
import Layout from 'src/layouts';
import Page from 'src/components/Page';
import { useRouter } from 'next/router';
import { PATH_APP } from 'src/routes/paths';

export interface OrganizationContextType {
  organization: Organization; // non-nullable
  users: User[];
  isLoading: boolean;
  refetchData: () => Promise<void>;
  currentUser: User;
}

interface OrganizationProviderProps {
  children: ReactNode;
  organizationName?: string;
  organizationId?: string;
  variant?: 'app' | 'global'; // 'app': 通常ページ(デフォルト), 'global': グローバル管理画面
}
const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({
  children,
  organizationName,
  organizationId,
  variant = 'app',
}) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { reportError } = useError();
  const authContext = useContext(AuthContext);
  const { user: cognitoUser, isInitialized: authInitialized, isAuthenticated } = authContext || {};
  const router = useRouter();

  // 評価結果のfullscreenページかどうかを正確に判定
  const isReviewResultFullscreenPage =
    router.pathname === PATH_APP.me.reviews.review.reviewResultNewTab;

  // 組織取得関数
  const fetchOrganization = useCallback(async (): Promise<Organization | undefined> => {
    try {
      if (organizationId) {
        const result = await awsApiAdapter.graphqlQuery<GetOrganizationQuery>({
          query: gql(queries.getOrganization),
          variables: {
            id: organizationId,
          },
        });
        const org = result.data?.getOrganization;
        if (org) {
          return org as Organization;
        }
      } else if (organizationName) {
        const result = await awsApiAdapter.graphqlQuery<ListOrganizationsByNameQuery>({
          query: gql(queries.listOrganizationsByName),
          variables: {
            name: organizationName,
          },
        });

        const organizations = (result.data?.listOrganizationsByName?.items ?? []).filter(
          nonNullable
        ) as Organization[];
        if (organizations.length > 0) {
          return organizations[0]; // 最初のマッチした組織を返す
        }
      }
    } catch (error) {
      console.error('組織取得エラー:', error);
    }
  }, [organizationName, organizationId]);

  // ユーザー取得関数（ページネーション対応）
  const fetchUsers = useCallback(async (organizationId: string): Promise<User[]> => {
    try {
      let allUsers: User[] = [];
      let nextToken: string | null = null;

      do {
        const variables: ListUserByOrganizationIDQueryVariables = {
          organizationID: organizationId,
          limit: 1000,
          nextToken,
        };
        const result = await awsApiAdapter.graphqlQuery<ListUserByOrganizationIDQuery>({
          query: gql(queries.listUserByOrganizationID as any) as any,
          variables,
        });

        const users = (result.data?.listUserByOrganizationID?.items.filter(nonNullable) ??
          []) as User[];
        allUsers = [...allUsers, ...users];
        nextToken = result.data?.listUserByOrganizationID?.nextToken || null;
      } while (nextToken);

      return allUsers;
    } catch (error) {
      console.error('ユーザー取得エラー:', error);
      throw new Error('ユーザー一覧の取得に失敗しました');
    }
  }, []);

  // 現在ログイン中のユーザーを取得する関数
  const fetchCurrentUser = useCallback(async (): Promise<User | null> => {
    console.log('cognitoUser?.username', cognitoUser?.username);
    console.log('authInitialized', authInitialized);
    console.log('isAuthenticated', isAuthenticated);

    // 認証が初期化されていない場合は待機
    if (!authInitialized) {
      return null;
    }

    // 認証されていない場合はnullを返す
    if (!isAuthenticated || !cognitoUser?.username) {
      return null;
    }

    const user = await awsApiAdapter.graphqlQuery<GetUserQuery>({
      query: gql(queries.getUser as any) as any,
      variables: {
        id: cognitoUser.username,
      },
    });
    return user?.data?.getUser as User | null;
  }, [cognitoUser?.username, authInitialized, isAuthenticated]);

  // データ取得メイン関数
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      // 1. 組織を取得
      const org = await fetchOrganization();
      if (!org) {
        console.error('指定された組織が見つかりません');
        return;
      }

      setOrganization(org);

      // 2. ユーザー一覧を取得
      const userList = await fetchUsers(org.id);
      setUsers(userList);

      // 3. 現在ログイン中のユーザーを取得
      const currentUser = await fetchCurrentUser();
      console.log('currentUser', currentUser);

      // 認証されている場合は、currentUserが取得できない場合もエラーとして扱わない
      // （認証情報とDB情報の不整合の可能性があるため）
      if (currentUser) {
        setCurrentUser(currentUser);
      } else if (authInitialized && isAuthenticated) {
        console.warn('認証されているがユーザー情報を取得できませんでした');
        // この場合でもエラーを投げずに進行する
        setCurrentUser(null);
      }
    } catch (error) {
      reportError(error);
      console.error('データ取得エラー:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchOrganization,
    fetchUsers,
    fetchCurrentUser,
    authInitialized,
    isAuthenticated,
    reportError,
  ]);

  // リフェッチ関数
  const refetchData = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // 初回データ取得 - 認証が初期化されてから実行
  useEffect(() => {
    if ((organizationName || organizationId) && authInitialized) {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }
      // 認証されている場合のみデータを取得
      fetchData();
    }
  }, [organizationName, organizationId, authInitialized, isAuthenticated, fetchData]);

  // デバッグ用ログ（開発環境のみ）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('OrganizationProvider State:', {
        organizationName,
        organizationId,
        organization: organization?.name,
        currentUser: currentUser,
        usersCount: users.length,
        isLoading,
      });
    }
  }, [organizationName, organizationId, organization, users.length, isLoading, currentUser]);

  // エラー表示用のメモ化されたコンポーネント
  const organizationNotFoundComponent = useMemo(
    () => (
      <Layout>
        <Page title="組織が見つかりません">
          <div className="flex justify-center items-center h-screen">
            <div className="text-center">
              <h2 className="mt-2 text-xl font-semibold text-gray-900">組織が見つかりません</h2>
            </div>
          </div>
        </Page>
      </Layout>
    ),
    []
  );

  const userNotLoggedInComponent = useMemo(
    () => (
      <Layout>
        <Page title="ログインが必要です">
          <div className="flex justify-center items-center h-screen">
            <div className="text-center">
              <h2 className="mt-2 text-xl font-semibold text-gray-900">ログインしていません</h2>
            </div>
          </div>
        </Page>
      </Layout>
    ),
    []
  );

  // 認証が初期化されていない場合はローディング
  if (!authInitialized || isLoading) {
    const loadingValue: OrganizationContextType = {
      organization: null as any, // ローディング中はnull
      users: [],
      isLoading: true,
      refetchData: async () => {},
      currentUser: null as any, // ローディング中はnull
    };

    if (isReviewResultFullscreenPage || variant === 'global') {
      // 評価結果fullscreenページまたはグローバルページの場合は、ローディング状態のコンテキストを提供
      return (
        <OrganizationContext.Provider value={loadingValue}>{children}</OrganizationContext.Provider>
      );
    }

    // 通常ページもProviderでラップする（Layout内のNavbarがuseOrganizationを使用するため）
    return (
      <OrganizationContext.Provider value={loadingValue}>
        <Layout>
          <Page title="読み込み中">
            <Skeleton variant="rectangular" height={442} />
          </Page>
        </Layout>
      </OrganizationContext.Provider>
    );
  }

  // 組織が見つからない場合
  if (!organization) {
    const errorValue: OrganizationContextType = {
      organization: null as any,
      users: [],
      isLoading: false,
      refetchData: async () => {},
      currentUser: null as any,
    };
    return (
      <OrganizationContext.Provider value={errorValue}>
        {organizationNotFoundComponent}
      </OrganizationContext.Provider>
    );
  }

  // 認証されていない場合
  if (!isAuthenticated) {
    const errorValue: OrganizationContextType = {
      organization: null as any,
      users: [],
      isLoading: false,
      refetchData: async () => {},
      currentUser: null as any,
    };
    return (
      <OrganizationContext.Provider value={errorValue}>
        {userNotLoggedInComponent}
      </OrganizationContext.Provider>
    );
  }

  // 認証されているがcurrentUserが取得できていない場合
  if (!currentUser) {
    // グローバルページの場合は、currentUserなしでコンテキストを提供（グローバル管理画面では不要）
    if (variant === 'global') {
      const value: OrganizationContextType = {
        organization,
        users,
        isLoading: false,
        refetchData,
        currentUser: null as any, // グローバルページではcurrentUserは不要
      };
      return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
    }

    if (isReviewResultFullscreenPage) {
      // 評価結果fullscreenページの場合は、ローディング状態のコンテキストを提供
      const loadingValue: OrganizationContextType = {
        organization: organization!,
        users,
        isLoading: true,
        refetchData,
        currentUser: null as any, // ローディング中はnull
      };
      return (
        <OrganizationContext.Provider value={loadingValue}>{children}</OrganizationContext.Provider>
      );
    }
    // 通常ページもProviderでラップする（Layout内のNavbarがuseOrganizationを使用するため）
    const loadingValue: OrganizationContextType = {
      organization: organization!,
      users,
      isLoading: true,
      refetchData,
      currentUser: null as any,
    };
    return (
      <OrganizationContext.Provider value={loadingValue}>
        <Layout>
          <Page title="ユーザー情報読み込み中">
            <Skeleton variant="rectangular" height={442} />
          </Page>
        </Layout>
      </OrganizationContext.Provider>
    );
  }

  // すべてのデータが揃った場合のみコンテキストを提供
  const value: OrganizationContextType = {
    organization,
    users,
    isLoading,
    refetchData,
    currentUser,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization は OrganizationProvider 内で使用してください');
  }
  return context;
};
